import { Box, Loader, Text, Tooltip, useMantineColorScheme } from "@mantine/core";
import { useState } from "react";
import { useDisclaimer } from "src/contexts/DisclaimerContext";
import { getDisclaimerMessages } from "src/utils/referencesHandler";
import type { Reference } from "../db";
import { useApiStreamingState } from "../hooks/useChatCompletion";
import { useArtifactHandler } from "../utils/artifactHandler";
import { ReferenceTooltip } from "./ReferenceViewer";

interface InlineReferenceProps {
    reference: Reference;
    index: string;
    chatId?: string;
    showDisclaimer?: boolean;
}

export const InlineReference = ({
    reference,
    index,
    chatId,
    showDisclaimer = false,
}: InlineReferenceProps) => {
    const isStreaming = useApiStreamingState(chatId);
    const [isLoading, setIsLoading] = useState(false);
    const { handleArtifactClick, getFullUrl } = useArtifactHandler();
    const { openDisclaimer } = useDisclaimer();
    const { colorScheme } = useMantineColorScheme();

    return (
        <Tooltip
            disabled={isStreaming}
            closeDelay={600}
            openDelay={100}
            withArrow
            position="top"
            styles={{
                tooltip: {
                    pointerEvents: "auto",
                    backgroundColor: "transparent",
                    padding: 0,
                    boxShadow: "none",
                    maxWidth: "350px",
                    minWidth: "200px",
                },
            }}
            label={<ReferenceTooltip reference={reference} showDisclaimer={showDisclaimer} />}
        >
            {reference.source ? (
                <Text
                    component="a"
                    data-testid={`${reference.reference_id}-inline-reference`}
                    href={getFullUrl(reference.source)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={async (e) => {
                        if (isLoading) {
                            e.preventDefault();
                            return;
                        }
                        setIsLoading(true);
                        try {
                            await handleArtifactClick(
                                e,
                                reference.source,
                                showDisclaimer
                                    ? getDisclaimerMessages(reference.dataSourceType)
                                    : [],
                                openDisclaimer,
                            );
                        } finally {
                            setIsLoading(false);
                        }
                    }}
                    style={(theme) => ({
                        display: "inline-block",
                        backgroundColor:
                            colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[1],
                        padding: "2px 6px",
                        margin: "2px",
                        borderRadius: theme.radius.sm,
                        cursor: isLoading ? "default" : "pointer",
                        textDecoration: "none",
                        fontSize: "0.6rem",
                        color: "inherit",
                        position: "relative",
                        opacity: isLoading ? 0.7 : 1,
                        "&:hover": {
                            backgroundColor: !isLoading ? theme.colors.purple?.[1] : undefined,
                            color: !isLoading ? "#fff" : undefined,
                        },
                    })}
                >
                    {isLoading && (
                        <Box
                            style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                zIndex: 1,
                            }}
                        >
                            <Loader size="xs" />
                        </Box>
                    )}
                    <span style={{ visibility: isLoading ? "hidden" : "visible" }}>{index}</span>
                </Text>
            ) : (
                <Text
                    component="span"
                    style={(theme) => ({
                        display: "inline-block",
                        backgroundColor:
                            colorScheme === "dark" ? theme.colors.dark[4] : theme.colors.gray[1],
                        padding: "2px 6px",
                        margin: "2px",
                        borderRadius: theme.radius.sm,
                        cursor: "pointer",
                        textDecoration: "none",
                        fontSize: "0.6rem",
                        color: "inherit",
                        "&:hover": { backgroundColor: theme.colors.purple?.[1], color: "#fff" },
                    })}
                >
                    {index}
                </Text>
            )}
        </Tooltip>
    );
};
